import React, { useEffect, useState, useCallback } from "react";
import { format } from "date-fns";
import DashboardLayout from "./DashboardLayout/DasboardLayout";
import { API_URL } from "./config";
import { Users, AlertTriangle } from "lucide-react";

// Terminology (corrected by the user 2026-09-13, kept precise throughout this screen):
//   Issued — bonus credited into a wallet (UW or promo wallet). Value still inside XGO.
//   Spent/Used — the issued value was drawn down (e.g. a ride discount). Still inside XGO.
//   Paid — value actually left XGO (a real cash withdrawal). Not shown here — with the current
//   schema there's no way to trace a specific bonus through to an actual withdrawal (UW is a
//   shared, unattributed pool once credited), so no "Paid" figure is invented for this screen.

function formatMoney(n) {
  return `$${(Number(n) || 0).toFixed(2)}`;
}

function userLabel(usersById, id) {
  const u = usersById.get(id);
  return u ? `${u.username || id} (${id})` : id;
}

function IssuedBadge({ issued }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs ${issued ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
      {issued ? "Issued" : "Not yet issued"}
    </span>
  );
}

function SpentBadge({ row }) {
  if (row.referee_bonus_status === "not_issued") {
    return <span className="text-xs text-gray-400">—</span>;
  }
  if (row.referee_bonus_status === "spent") {
    const detail = row.referee_bonus_spent_detail;
    return (
      <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800" title={detail ? `Trip ${detail.trip_id} on ${detail.spent_at}` : ""}>
        Spent{detail ? ` — $${Number(detail.amount).toFixed(2)} on trip ${detail.trip_id}` : ""}
      </span>
    );
  }
  return <span className="px-2 py-0.5 rounded-full text-xs bg-gray-200 text-gray-700">Unspent</span>;
}

const ReferralTracking = () => {
  const [referrals, setReferrals] = useState([]);
  const [usersById, setUsersById] = useState(new Map());
  const [budgets, setBudgets] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [orphanedCredits, setOrphanedCredits] = useState([]);
  const [loading, setLoading] = useState(true);

  const [userFilter, setUserFilter] = useState("");
  const [issuedFilter, setIssuedFilter] = useState("all");
  const [spentFilter, setSpentFilter] = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/enhanced-referral/all-referrals`).then((r) => r.json()),
      fetch(`${API_URL}/users/`).then((r) => r.json()),
      fetch(`${API_URL}/promotions/wallet/budgets`).then((r) => r.json()),
      fetch(`${API_URL}/promotions/configurations`).then((r) => r.json()),
      fetch(`${API_URL}/enhanced-referral/orphaned-bonus-credits`).then((r) => r.json()),
    ]).then(([referralsRes, usersRes, budgetsRes, configsRes, orphanedRes]) => {
      setReferrals(referralsRes.data || []);
      const map = new Map();
      (usersRes || []).forEach((u) => {
        [u.userid, u.driver_id, u.customerid].forEach((id) => {
          if (id && String(id) !== "0") map.set(id, u);
        });
      });
      setUsersById(map);
      setBudgets(budgetsRes.data || []);
      setConfigs(configsRes.data || []);
      setOrphanedCredits((orphanedRes.data || []).filter((c) => !c.has_tracking_row));
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = referrals.filter((r) => {
    if (userFilter && r.referrer_id !== userFilter && r.referred_user_id !== userFilter) return false;
    if (issuedFilter === "issued" && !r.referrer_bonus_issued) return false;
    if (issuedFilter === "not_issued" && r.referrer_bonus_issued) return false;
    if (spentFilter === "spent" && r.referee_bonus_status !== "spent") return false;
    if (spentFilter === "unspent" && r.referee_bonus_status !== "unspent") return false;
    return true;
  });

  // "Due" — referrer bonuses not yet issued (awaiting the referred user's first ride). A real
  // obligation the promo_budgets row alone can't show, since it only reflects what's already
  // been issued, not what's been earned-but-not-yet-triggered.
  const dueByPromo = referrals
    .filter((r) => !r.referrer_bonus_issued)
    .reduce((sum, r) => sum + (Number(r.referrer_bonus_amount) || 0), 0);

  return (
    <DashboardLayout>
      <div className="p-6 bg-white dark:bg-gray-800 min-h-screen">
        <h1 className="mb-1 flex items-center gap-2 text-3xl font-bold text-gray-800 dark:text-white">
          <Users size={28} /> Referral Tracking
        </h1>
        <p className="mb-6 text-gray-600 dark:text-gray-400">
          Who referred whom, whether each bonus has been issued, and whether an issued bonus has
          actually been spent. "Issued" means credited to a wallet — not cash that has left XGO.
        </p>

        {/* Obligations overview */}
        <h2 className="mb-3 text-xl font-bold text-gray-800 dark:text-white">Bonus Obligations by Promo</h2>
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {configs.map((c) => {
            const budget = budgets.find((b) => b.promo_id === c.promo_id && b.config_type === c.config_type);
            const issued = budget ? Number(budget.spent) : 0;
            const remaining = budget ? Number(budget.remaining) : 0;
            const due = c.promo_id === "PROMO-FIRST-RIDE-001" ? dueByPromo : 0;
            const noPayoutCode = !["PROMO-REFR-FRIEND-001", "PROMO-FIRST-RIDE-001"].includes(c.promo_id);
            return (
              <div key={`${c.promo_id}-${c.config_type}`} className="rounded-lg border p-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-bold">{c.promo_id}</span>
                  <span className="text-xs text-gray-500">{c.config_type}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div><p className="text-gray-500">Issued</p><p className="font-semibold">{formatMoney(issued)}</p></div>
                  <div><p className="text-gray-500">Due</p><p className="font-semibold text-amber-600">{formatMoney(due)}</p></div>
                  <div><p className="text-gray-500">Budget left</p><p className={`font-semibold ${remaining < due ? "text-red-600" : "text-green-600"}`}>{formatMoney(remaining)}</p></div>
                </div>
                {remaining < due && (
                  <p className="mt-2 flex items-center gap-1 text-xs text-red-600">
                    <AlertTriangle size={12} /> Budget can't cover known obligations
                  </p>
                )}
                {noPayoutCode && (
                  <p className="mt-2 text-xs text-gray-400">No payout code exists for this promo yet — Issued will always be $0.</p>
                )}
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <h2 className="mb-3 text-xl font-bold text-gray-800 dark:text-white">Referrals</h2>
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">User ID (referrer or referred)</label>
            <input
              type="text"
              placeholder="e.g. AAA-100120"
              className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value.trim())}
            />
          </div>
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">Referrer bonus</label>
            <select className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" value={issuedFilter} onChange={(e) => setIssuedFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="issued">Issued</option>
              <option value="not_issued">Not yet issued</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">Referee bonus spent?</label>
            <select className="p-2 border rounded-lg focus:ring-2 focus:ring-blue-500" value={spentFilter} onChange={(e) => setSpentFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="spent">Spent</option>
              <option value="unspent">Unspent</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto shadow-md rounded-lg">
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
            <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-300">
              <tr>
                <th className="px-4 py-3">Referrer</th>
                <th className="px-4 py-3">Referred User</th>
                <th className="px-4 py-3">Signup Date</th>
                <th className="px-4 py-3">Referrer Bonus</th>
                <th className="px-4 py-3">Referee Bonus</th>
                <th className="px-4 py-3">Referee Spent?</th>
                <th className="px-4 py-3">First Ride</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.tracking_id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                  <td className="px-4 py-3">{userLabel(usersById, r.referrer_id)}</td>
                  <td className="px-4 py-3">
                    {userLabel(usersById, r.referred_user_id)}
                    {r.referee_role_attribution_caveat && (
                      <span className="ml-1 text-amber-600" title={r.referee_role_attribution_caveat}>⚠</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{r.signup_date ? format(new Date(r.signup_date), "MMM dd, yyyy") : "N/A"}</td>
                  <td className="px-4 py-3">
                    {formatMoney(r.referrer_bonus_amount)} <IssuedBadge issued={r.referrer_bonus_issued} />
                  </td>
                  <td className="px-4 py-3">
                    {formatMoney(r.referee_bonus_amount)} <IssuedBadge issued={r.referee_bonus_issued} />
                  </td>
                  <td className="px-4 py-3"><SpentBadge row={r} /></td>
                  <td className="px-4 py-3">
                    {r.first_ride_completed ? `Yes — ${format(new Date(r.first_ride_date), "MMM dd, yyyy")}` : "Not yet"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!loading && filtered.length === 0 && (
          <div className="text-center py-6 text-gray-500 dark:text-gray-400">
            No referrals {referrals.length === 0 ? "have happened yet" : "match the selected filters"}.
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReferralTracking;
